<script>
import ContactConversations from 'dashboard/routes/dashboard/conversation/ContactConversations.vue';
import ContactInfo from 'dashboard/routes/dashboard/conversation/contact/ContactInfo.vue';
import ContactLabel from 'dashboard/routes/dashboard/contacts/components/ContactLabels.vue';
import ContactRelatedCompanies from 'dashboard/routes/dashboard/contacts/components/ContactRelatedCompanies.vue';
import ContactAction from 'dashboard/routes/dashboard/conversation/ContactAction.vue';
import CustomAttributes from 'dashboard/routes/dashboard/conversation/customAttributes/CustomAttributes.vue';
import DashboardIcon from 'shared/components/FluentIcon/DashboardIcon.vue';
import PanelHeader from 'dashboard/routes/dashboard/conversation/sidebar/PanelHeader.vue';
import PanelSection from 'dashboard/routes/dashboard/conversation/sidebar/PanelSection.vue';
import draggable from 'vuedraggable';
import {
  useUISettings,
  DEFAULT_CONTACT_SIDEBAR_ITEMS_ORDER,
} from 'dashboard/composables/useUISettings';
import {
  getUserPermissions,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper';
import { mapGetters } from 'vuex';

export default {
  components: {
    ContactConversations,
    ContactInfo,
    ContactLabel,
    ContactRelatedCompanies,
    ContactAction,
    CustomAttributes,
    DashboardIcon,
    Draggable: draggable,
    PanelHeader,
    PanelSection,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    onClose: {
      type: Function,
      default: () => {},
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    closeIconName: {
      type: String,
      default: 'dismiss',
    },
    isStandalonePage: {
      type: Boolean,
      default: false,
    },
    showDetachButton: {
      type: Boolean,
      default: false,
    },
    // Na tela do proprio contato o atalho "Abrir contato" levaria a pagina em
    // que o usuario ja esta.
    hideProfileLink: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { updateUISettings, contactSidebarItemsOrder } = useUISettings();

    return {
      updateUISettings,
      contactSidebarItemsOrder,
    };
  },
  data() {
    return {
      dragEnabled: true,
      contactSidebarItems: [],
      dragging: false,
      canEditRelatedCompanies: false,
      canAssociateCompany: false,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      currentAccountId: 'getCurrentAccountId',
    }),
    userPermissions() {
      return getUserPermissions(this.currentUser, this.currentAccountId);
    },
    canViewCustomAttributes() {
      return hasPermissions(
        ['administrator', 'custom_attribute_view', 'custom_attribute_manage'],
        this.userPermissions
      );
    },
    canViewLabels() {
      return hasPermissions(
        ['administrator', 'label_view', 'label_manage'],
        this.userPermissions
      );
    },
  },
  watch: {
    // O vuedraggable recalcula o mapa DOM -> item so no mount, quando a
    // REFERENCIA da lista muda e no fim de um arraste. As secoes dependem do
    // contato: com o painel aberto e outro contato selecionado (ou o contato
    // terminando de carregar), elas entram e saem sem a lista mudar de
    // referencia, e o primeiro arraste gravaria a ordem uma posicao fora.
    // Mesma correcao do `ContactPanel.vue` da conversa.
    'contact.id': function watchContactId() {
      this.contactSidebarItems = [...this.contactSidebarItems];
    },
  },
  mounted() {
    // Cria uma cópia mutável local para que o vuedraggable possa reordenar
    // sem tentar modificar o array frozen retornado pelo composable/Vuex.
    this.contactSidebarItems = [...this.contactSidebarItemsOrder];
  },
  methods: {
    // `ref` dentro de `v-for` vira array.
    openRelatedCompaniesAssociation() {
      const [companies] = [].concat(this.$refs.relatedCompanies || []);
      companies?.openAssociateModal();
    },
    openRelatedCompaniesEditor() {
      const [companies] = [].concat(this.$refs.relatedCompanies || []);
      companies?.openEditModal();
    },
    // O `v-if` fica no filho DIRETO do draggable, e nao dentro do PanelSection:
    // secao escondida por permissao nao deixa um cartao vazio na lista. O
    // `v-for` precisa percorrer o array INTEIRO passado em `:list` -- filtrar
    // ele quebraria o mapeamento do vuedraggable e o arraste moveria o item
    // errado.
    isSectionVisible(name) {
      if (!this.contact?.id) {
        return false;
      }
      if (name === 'contact_attributes') {
        return this.canViewCustomAttributes;
      }
      if (name === 'contact_labels') {
        return this.canViewLabels;
      }
      return true;
    },
    sectionTitle(name) {
      const titles = {
        contact_assignment: this.$t(
          'CONVERSATION_SIDEBAR.SECTIONS.CONTACT_ASSIGNMENT'
        ),
        related_companies: this.$t(
          'CONTACT_PANEL.SIDEBAR_SECTIONS.RELATED_COMPANIES'
        ),
        contact_attributes: this.$t(
          'CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES'
        ),
        contact_labels: this.$t(
          'CONTACT_PANEL.SIDEBAR_SECTIONS.CONTACT_LABELS'
        ),
        previous_conversation: this.$t(
          'CONTACT_PANEL.SIDEBAR_SECTIONS.PREVIOUS_CONVERSATIONS'
        ),
      };
      return titles[name] || '';
    },
    // Cada secao e um stacking context com z DECRESCENTE: a de cima cobre a de
    // baixo (popovers e seletores abrem para baixo).
    getSidebarItemZIndex(index, total) {
      return total - index;
    },
    resetOrder() {
      this.contactSidebarItems = [...DEFAULT_CONTACT_SIDEBAR_ITEMS_ORDER];
      this.updateUISettings({
        contact_sidebar_items_order: this.contactSidebarItems,
      });
    },
    onDragEnd() {
      this.dragging = false;
      this.updateUISettings({
        contact_sidebar_items_order: this.contactSidebarItems,
      });
    },
  },
};
</script>

<template>
  <!-- Backdrop: visible only on < lg -->
  <div
    :class="
      isStandalonePage
        ? 'flex h-full w-full self-stretch'
        : 'lg:relative lg:w-[24rem] lg:min-w-[24rem] lg:max-w-[24rem] lg:h-full lg:self-stretch'
    "
  >
    <div
      v-if="!isStandalonePage"
      class="fixed inset-0 bg-black/30 z-40 lg:hidden"
      @click="onClose"
    />
    <div
      class="flex flex-col text-sm bg-surface text-body contact-info-panel"
      :class="[
        isStandalonePage
          ? 'relative z-10 flex h-full w-full flex-col'
          : 'fixed inset-y-0 right-0 z-50 w-full sm:w-[24rem] border-line-faint lg:absolute lg:inset-0 lg:w-auto lg:z-auto',
        !isStandalonePage && 'h-full',
        !isStandalonePage &&
          (showAvatar ? 'border-l border-solid' : 'border-r border-solid'),
      ]"
    >
      <!-- Cabecalho FIXO, fora do scroll: e o unico lugar que diz de quem e o
           painel, e o fechar precisa continuar alcancavel com a lista rolada.
           O mesmo cabecalho do painel da conversa (#1243). -->
      <div
        v-if="showAvatar"
        class="flex items-center px-3 border-b shrink-0 min-h-title-band border-line-faint"
      >
        <PanelHeader
          :contact="contact"
          :show-detach-button="showDetachButton"
          :show-close-button="showCloseButton"
          :close-icon="closeIconName"
          close-always-visible
          @togglePanel="onClose"
        />
      </div>

      <div class="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
        <!-- Dados do contato e acoes: fixo no topo, sem punho de arraste. -->
        <div class="border-b border-line-faint">
          <PanelSection
            :title="$t('CONTACT_PANEL.SIDEBAR_SECTIONS.CONTACT_DETAILS')"
          >
            <ContactInfo
              :contact="contact"
              :show-avatar="false"
              :show-close-button="!showAvatar && showCloseButton"
              :show-detach-button="!showAvatar && showDetachButton"
              :close-icon-name="closeIconName"
              :hide-profile-link="hideProfileLink"
              compact
              @panelClose="onClose"
              @togglePanel="onClose"
            />
          </PanelSection>
        </div>

        <Draggable
          :list="contactSidebarItems"
          :disabled="!dragEnabled"
          animation="200"
          class="flex flex-col"
          ghost-class="ghost"
          handle=".drag-handle"
          @start="dragging = true"
          @end="onDragEnd"
        >
          <transition-group>
            <template v-for="(element, index) in contactSidebarItems">
              <div
                v-if="isSectionVisible(element.name)"
                :key="element.name"
                class="relative border-b border-line-faint last:border-b-0"
                :style="{
                  zIndex: getSidebarItemZIndex(
                    index,
                    contactSidebarItems.length
                  ),
                }"
              >
                <PanelSection draggable :title="sectionTitle(element.name)">
                  <!-- O lapis mora no cabecalho da secao (mesmo lugar das
                       acoes das outras secoes do painel), e nao numa linha
                       propria acima do cartao da empresa. -->
                  <template #actions>
                    <woot-button
                      v-if="
                        element.name === 'related_companies' &&
                        canAssociateCompany
                      "
                      icon="add"
                      size="tiny"
                      variant="smooth"
                      color-scheme="secondary"
                      :title="
                        $t('CONTACT_PANEL.SIDEBAR_SECTIONS.RELATE_COMPANY')
                      "
                      @click="openRelatedCompaniesAssociation"
                    />
                    <woot-button
                      v-if="
                        element.name === 'related_companies' &&
                        canEditRelatedCompanies
                      "
                      icon="edit"
                      size="tiny"
                      variant="smooth"
                      color-scheme="secondary"
                      :title="
                        $t(
                          'CONTACT_PANEL.SIDEBAR_SECTIONS.EDIT_RELATED.EDIT_BUTTON'
                        )
                      "
                      @click="openRelatedCompaniesEditor"
                    />
                  </template>
                  <ContactAction
                    v-if="element.name === 'contact_assignment'"
                    :contact-id="contact.id"
                    :fallback-contact="contact"
                  />
                  <ContactRelatedCompanies
                    v-else-if="element.name === 'related_companies'"
                    ref="relatedCompanies"
                    :contact-id="contact.id"
                    @editableChange="canEditRelatedCompanies = $event"
                    @associableChange="canAssociateCompany = $event"
                  />
                  <CustomAttributes
                    v-else-if="element.name === 'contact_attributes'"
                    :contact-id="contact.id"
                    attribute-type="contact_attribute"
                    attribute-from="contact_panel"
                    :empty-state-message="
                      $t('CONTACT_PANEL.SIDEBAR_SECTIONS.NO_RECORDS_FOUND')
                    "
                    panel
                  />
                  <ContactLabel
                    v-else-if="element.name === 'contact_labels'"
                    :contact-id="contact.id"
                    class="contact-labels"
                  />
                  <ContactConversations
                    v-else-if="element.name === 'previous_conversation'"
                    :contact-id="contact.id"
                    conversation-id=""
                  />
                </PanelSection>
              </div>
            </template>
          </transition-group>
        </Draggable>

        <!-- Acao sobre a lista inteira: fora do `<Draggable>` (nao e uma
             secao arrastavel) e dentro do scroll, para nao cobrar altura fixa
             da coluna. Mesma faixa do painel da conversa. -->
        <div class="flex justify-center px-4 py-3 border-t border-line-faint">
          <button
            type="button"
            class="flex items-center gap-2 px-3 py-2 text-xs font-medium transition-colors duration-150 border rounded-lg text-muted border-line bg-surface-sunken dark:bg-surface-raised hover:bg-line-faint dark:hover:bg-surface-hover hover:text-body"
            @click="resetOrder"
          >
            <DashboardIcon icon="arrow-clockwise-outline" size="14" />
            <span>{{ $t('CONVERSATION_SIDEBAR.PANEL_TABS.RESET_ORDER') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// As duas regras de etiqueta e a do campo personalizado repetem as do
// `ContactPanel.vue` da conversa, e moram FORA de um `::v-deep` aninhado pelo
// mesmo motivo la documentado: com o atributo de escopo no `.contact-info-panel`
// a especificidade sobe para (0,4,0) e passa a regra global do `Label.vue` /
// `AddLabel.vue`, em vez de empatar com ela e depender da ordem dos bundles.
.contact-info-panel ::v-deep .label.smooth {
  @apply bg-surface-sunken;
}

.dark .contact-info-panel ::v-deep .label.smooth {
  @apply bg-surface-sunken;
}

.contact-info-panel ::v-deep .label--add.button {
  @apply h-6 gap-1.5 px-2.5 py-0 text-xs font-medium border border-dashed
    rounded-full shadow-none bg-transparent text-muted border-line-strong;

  &:hover {
    @apply bg-brand/5 text-brand-text border-brand;
  }
}

.contact-info-panel ::v-deep {
  // O primeiro campo personalizado nao soma o seu `py-2.5` ao respiro do
  // titulo da secao.
  .custom-attribute:first-child {
    @apply pt-0;
  }

  .label,
  .label--add.button {
    @apply align-middle;
  }

  .label--add.button {
    @apply mb-1 mr-0;
  }
}

/* Estilização da scrollbar para melhor UX */
.custom-scrollbar {
  overflow-y: auto;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.4);
    border-radius: 4px;

    &:hover {
      background: rgba(148, 163, 184, 0.7);
    }
  }
}
</style>
